/** A time typed in shorthand, as hours and minutes on a 24-hour clock. */
export interface ShorthandTime {
  hours: number;
  minutes: number;
}

/**
 * What a shorthand entry may look like: an hour, an optional separator, optional
 * minutes, and an optional meridiem.
 *
 * The separator list carries `.` and `,` because both sit on the numeric keypad
 * where `:` does not — someone entering times in bulk never leaves it.
 */
const SHORTHAND =
  /^(\d{1,4})\s*(?:[:.,]\s*(\d{1,2}))?\s*([ap])\.?m?\.?$|^(\d{1,4})\s*(?:[:.,]\s*(\d{1,2}))?$/i;

const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
const HOURS_PER_MERIDIEM = 12;

/**
 * Splits a run of digits typed without a separator. The reading follows the
 * length, the way a clock is spoken: `6` is six o'clock, `830` is half eight,
 * `1830` is the same in 24-hour form.
 */
function splitDigits(digits: string): ShorthandTime | null {
  if (digits.length <= 2) {
    return { hours: Number(digits), minutes: 0 };
  }
  const boundary = digits.length - 2;
  return {
    hours: Number(digits.slice(0, boundary)),
    minutes: Number(digits.slice(boundary)),
  };
}

/**
 * Applies a typed meridiem to a 12-hour reading. Noon and midnight are the two
 * the arithmetic does not reach on its own: 12 AM is 0 and 12 PM stays 12.
 */
function applyMeridiem(hours: number, meridiem: 'a' | 'p'): number | null {
  if (hours < 1 || hours > HOURS_PER_MERIDIEM) {
    return null;
  }
  const onTheClock = hours % HOURS_PER_MERIDIEM;
  return meridiem === 'p' ? onTheClock + HOURS_PER_MERIDIEM : onTheClock;
}

/**
 * Reads a time typed without its punctuation — `1830`, `18.30`, `630p`, `6 pm`.
 *
 * It exists because the canonical format is the slowest way to enter a time:
 * a colon is a reach on every keyboard and a meridiem is three keystrokes for
 * one bit of information. Anything this does not recognise comes back as null
 * and is left to the strict parse, which owns the formats the field displays.
 *
 * Bare digits are always read as a 24-hour clock, whichever format the user
 * prefers, because the digits themselves say the hour: `18` can only be six in
 * the evening. A morning-or-evening hour needs the meridiem that resolves it,
 * which is exactly the case where typing one earns its keystrokes.
 */
export function parseShorthandTime(value: string): ShorthandTime | null {
  const match = SHORTHAND.exec(value.trim());
  if (!match) {
    return null;
  }

  const [
    ,
    meridiemDigits,
    meridiemMinutes,
    meridiem,
    plainDigits,
    plainMinutes,
  ] = match;
  const digits = meridiemDigits ?? plainDigits;
  const separatedMinutes = meridiemMinutes ?? plainMinutes;

  // A separator makes the split explicit; without one the digit count does it.
  const parsed =
    separatedMinutes === undefined
      ? splitDigits(digits)
      : { hours: Number(digits), minutes: Number(separatedMinutes) };

  if (parsed === null || parsed.minutes >= MINUTES_PER_HOUR) {
    return null;
  }

  if (meridiem) {
    const hours = applyMeridiem(
      parsed.hours,
      meridiem.toLowerCase() as 'a' | 'p',
    );
    return hours === null ? null : { hours, minutes: parsed.minutes };
  }

  return parsed.hours < HOURS_PER_DAY ? parsed : null;
}
